import firebase from 'firebase/app'
import 'firebase/auth'

import axios from 'axios'

import store from '@/vuex/store'
import { authReady, isAuthSettled, resetAuthGate, settleAuthGate } from '@/firebase'
import { setReauthenticator, unbindAllFirestoreRefs } from '@/services/firestoreAction'

/**
 * Firestore is no longer world readable, so the client has to genuinely sign in
 * to Firebase. Laravel remains the only identity provider: we exchange the
 * existing API session for a short-lived Firebase custom token and redeem it.
 *
 * The endpoint refuses anyone outside the Reviveaphone company, which is what
 * lets the security rules stay a single "is the caller signed in" check.
 */

const TOKEN_ENDPOINT = '/api/v5/auth/firebase-token';

// Technicians are always online, so these only have to absorb a brief 5G blip
// rather than genuine offline working.
const TOKEN_REQUEST_TIMEOUT_MS = 10000;
const MINT_ATTEMPTS = 3;
const MINT_BACKOFF_MS = [500, 1500];

const RESTORE_TIMEOUT_MS = 5000;

let inFlight = null;
let signingIn = null;
let generation = 0;

/**
 * The API key has to be read at call time. The store is hydrated from local
 * storage during boot and replaced wholesale on login, so anything that grabs
 * the key at module load sends a stale value - or null on a first login.
 */
function apiKey() {
    const user = store.getters.user;

    return user && user.api ? user.api.key : null;
}

function apiBaseUrl() {
    return process.env.VUE_APP_API_URI || 'https://admin-gb-repair.likewize.com';
}

/**
 * The uid Laravel mints tokens with: the user id, as a string.
 */
function laravelUserId() {
    const user = store.getters.user;

    return user && user.id ? String(user.id) : null;
}

/**
 * True for the endpoint's two terminal responses: 401 (no usable session) and
 * 403 (not a Reviveaphone user, or deactivated). Neither is worth retrying.
 */
export function isAccessDenied(error) {
    const status = error && error.response ? error.response.status : null;

    return status === 401 || status === 403;
}

export function isForbidden(error) {
    return !!error && !!error.response && error.response.status === 403;
}

/**
 * True when a failure is worth retrying. A dropped connection, a timeout or a
 * server error are all transient; 401 and 403 are answers, not failures.
 */
function isTransient(error) {
    if (isAccessDenied(error)) {
        return false;
    }

    // No response at all means the request never completed: DNS, connection
    // dropped, or the timeout above fired.
    if (!error || !error.response) {
        return true;
    }

    return error.response.status >= 500;
}

function pause(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * The v5 middleware reads `Authorization: Bearer <token>`; it ignores the
 * `?key=` query parameter the rest of this app leans on and would answer 401.
 * The key value itself is fine - the middleware falls back to legacy v4 tokens.
 */
function mintCustomToken() {
    const key = apiKey();

    if (!key) {
        return Promise.reject(new Error('Cannot mint a Firebase token without a Laravel API key.'));
    }

    return axios
        .post(apiBaseUrl() + TOKEN_ENDPOINT, {}, {
            headers: { Authorization: `Bearer ${key}` },
            // The router holds navigation on the auth gate, and the gate is only
            // settled when this request finishes. Without a timeout a hung
            // Laravel would leave the app on a blank screen indefinitely.
            timeout: TOKEN_REQUEST_TIMEOUT_MS
        })
        .then(response => response.data);
}

/**
 * Mint a token, retrying a connection that drops rather than giving up on the
 * first failure.
 *
 * Firestore refuses unauthenticated callers, so there is no useful half-signed-in
 * state to fall back to: every read would be denied anyway. Absorbing a brief
 * blip here is what stops that turning into a screenful of permission-denied.
 */
function mintWithRetry(attempt = 1) {
    return mintCustomToken().catch(error => {
        if (attempt >= MINT_ATTEMPTS || !isTransient(error)) {
            throw error;
        }

        return pause(MINT_BACKOFF_MS[attempt - 1]).then(() => mintWithRetry(attempt + 1));
    });
}

function exchangeToken() {
    return mintWithRetry()
        .then(({ token }) => firebase.auth().signInWithCustomToken(token))
        .then(credential => credential.user);
}

/**
 * Settle the gate, reopening it first if a previous attempt already closed it.
 * A promise only resolves once, so without the reset a second sign-in would
 * leave every Firestore caller waiting on the stale result of the first.
 */
function settleWith(user) {
    if (isAuthSettled()) {
        resetAuthGate();
    }

    settleAuthGate(user);

    return user;
}

/**
 * Mint a token and redeem it, reopening the auth gate for the duration so that
 * anything touching Firestore waits for the new session rather than racing it.
 */
function establish() {
    if (inFlight) {
        return inFlight;
    }

    if (isAuthSettled()) {
        resetAuthGate();
    }

    // A sign-out while this is in flight supersedes it: without this the stale
    // attempt would come back and quietly sign the user in again.
    const attempt = ++generation;

    // Only this attempt may clear `inFlight`. signOut() also clears it, so an
    // unconditional reset here would wipe a newer attempt and let a third call
    // start a second concurrent sign-in.
    const settleAttempt = () => {
        if (inFlight === promise) {
            inFlight = null;
        }
    };

    const promise = exchangeToken()
        .then(user => {
            settleAttempt();

            // Superseded. Leave the auth state alone - signOut() is global, and
            // a newer attempt may already own a valid session by now.
            if (attempt !== generation) {
                return null;
            }

            settleAuthGate(user);

            return user;
        })
        .catch(error => {
            settleAttempt();

            if (attempt === generation) {
                settleAuthGate(null);
            }

            throw error;
        });

    inFlight = promise;

    return inFlight;
}

/**
 * Resolve the Firebase session persisted in IndexedDB.
 *
 * `currentUser` is null until the SDK has restored that state asynchronously,
 * and signIn() runs in the same tick as initializeApp, so it is never populated
 * yet. Waiting for the first auth state callback is the only reliable read.
 */
function restoreSession() {
    return new Promise(resolve => {
        let done = false;

        const finish = user => {
            if (done) {
                return;
            }

            done = true;
            resolve(user || null);
        };

        // Nothing may depend on this callback actually firing: navigation is
        // held on the auth gate, so a Firebase SDK that never initialises must
        // not be able to freeze the app.
        const timer = setTimeout(() => finish(null), RESTORE_TIMEOUT_MS);

        const unsubscribe = firebase.auth().onAuthStateChanged(
            user => {
                clearTimeout(timer);
                unsubscribe();
                finish(user);
            },
            () => {
                clearTimeout(timer);
                unsubscribe();
                finish(null);
            }
        );
    });
}

/**
 * Sign in to Firebase, reusing an attempt that is already running.
 *
 * Resolves with the Firebase user, or null when there is no Laravel session to
 * exchange yet - the login screen, for instance. It only rejects when an actual
 * sign-in attempt fails, so callers can distinguish "not logged in" from
 * "logged in but refused".
 */
export function signIn() {
    if (inFlight) {
        return inFlight;
    }

    if (signingIn) {
        return signingIn;
    }

    // Nothing to exchange. Settle the gate so Firestore callers stop waiting;
    // they will be refused by the rules, which is the correct outcome.
    if (!apiKey()) {
        return Promise.resolve(settleWith(null));
    }

    const finished = user => {
        signingIn = null;

        return user;
    };

    // A sign-out during the restore supersedes this whole sequence.
    const attempt = generation;

    signingIn = restoreSession()
        .then(restored => {
            if (attempt !== generation) {
                return null;
            }

            // Reuse a persisted session rather than minting again on every page
            // load, so a slow or unreachable Laravel cannot stall the whole app.
            // Firebase renews its own ID tokens, so the session stays valid.
            //
            // The uid must match the Laravel user, otherwise a previous user's
            // session would be adopted by whoever logs in next.
            if (restored && restored.uid === laravelUserId()) {
                return settleWith(restored);
            }

            return establish();
        })
        .then(finished)
        .catch(error => {
            signingIn = null;

            throw error;
        });

    return signingIn;
}

/**
 * Force a fresh custom token and redeem it again. Used to recover from a single
 * permission-denied, which means the Firebase session has been lost or replaced.
 */
export function reauthenticate() {
    return establish();
}

/**
 * End the Firebase session.
 *
 * Listeners are detached first: a bound listener that outlives sign-out keeps
 * firing and starts throwing permission errors.
 */
export function signOut() {
    inFlight = null;
    signingIn = null;
    generation++;

    unbindAllFirestoreRefs();

    return firebase
        .auth()
        .signOut()
        .catch(() => {
            // Never let a Firebase failure block the user from logging out.
        })
        .then(() => {
            settleWith(null);
        });
}

export { authReady };

// Let the Firestore action wrapper recover from a lost Firebase session without
// importing the store, which would create a require cycle.
setReauthenticator(reauthenticate);

export default {
    authReady,
    isAccessDenied,
    isForbidden,
    reauthenticate,
    signIn,
    signOut
};
